import React, { FC, createContext, useContext, useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useLocation, useParams } from "react-router";
import { Route, Switch, useHistory } from "react-router-dom";
import { getErrorOrderNotClosed } from "src/utils/ordersNotClosedAlert";
import { OrderNotClosedApiError } from "src/utils/types";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { StocktakingApi } from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import { OrdersNotClosedAlert } from "../../../../components/OrdersNotClosedAlert";
import {
	TransactionContext,
	TransactionResourceType,
} from "../../../../components/Transaction/Context/TransactionContext";
import StocktakingForm from "../components/StocktakingForm/StocktakingForm";
import StocktakingStatusLabel from "../components/StocktakingStatusLabel";
import SubmenuStocktakingNavigation from "../components/SubmenuStocktakingNavigation";
import StocktakingDraftsTab from "../components/Tabs/StocktakingDrafts/StocktakingDraftsTab/StocktakingDraftsTab";
import StocktakingSummaryTab from "../components/Tabs/StocktakingSummaryTab";

interface MatchParams {
	stocktakingId: string;
}

interface StocktakingContextType {
	stocktaking: StocktakingApi;
	setCustomFieldsConfig: (customFields: CustomFieldTemplateApi[]) => void;
	customFieldsConfig: CustomFieldTemplateApi[];
	setStocktaking?: (stocktaking: StocktakingApi) => void;
}

export const StocktakingMobileActionsContext = createContext<MobileActionProps[]>([]);

export const StocktakingContext = createContext<StocktakingContextType>({
	stocktaking: {} as StocktakingApi,
	setCustomFieldsConfig: (customFields: CustomFieldTemplateApi[]) => {},
	customFieldsConfig: [],
	setStocktaking: (stocktaking: StocktakingApi) => {},
});

const OrganizationStocktakingPreviewPage: FC<RouteComponentProps> = (props) => {
	const { stocktakingId } = useParams<MatchParams>();
	const [stocktaking, setStocktaking] = useState<StocktakingApi | undefined>(undefined);
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const [alertMessage, setAlertMessage] = useState<string | JSX.Element | null>(null);
	const [showLogs, setShowLogs] = useState(false);
	const [orderNotClosedError, setOrderNotClosedError] = useState<OrderNotClosedApiError | undefined>(undefined);
	const { addFlash, addSuccessFlash } = useFlash();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const params = { include: "stock,tags,custom_fields,attachments" };
	const confirmation = useConfirmation();
	const url = props.match.url;
	const { setListItemId, setTransactionResourceType } = useContext(TransactionContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { isMobile } = useWindowSize();
	const { pathname } = useLocation();
	const displayNonListMobileActions = !pathname.includes("summary") && !pathname.includes("drafts");

	useEffect(() => {
		onFetchStocktaking();
		onFetchResources();
		setListItemId(stocktakingId);
		setTransactionResourceType(TransactionResourceType.STOCKTAKING);
	}, []);

	useEffect(() => {
		setAlertMessage(null);
		if (!history.location.pathname.includes("drafts") && !history.location.pathname.includes("summary")) {
			handleChangeTabTitle(`${stocktaking?.document_number ?? ""} | ${t("modules.stocktaking.header.title")}`);
		}
	}, [history.location.pathname]);

	const performAction = async ({
		action,
		customHandleError,
	}: {
		action: (() => Promise<void>) | (() => void);
		customHandleError?: (err: unknown) => void | Promise<void>;
	}) => {
		try {
			setAlertMessage(null);

			await action();
		} catch (err) {
			handleError.alert(err, addFlash);

			await customHandleError?.(err);
		}
	};

	const performUserAction = ({
		userAction,
		postAction,
		customHandleError,
	}: {
		userAction: () => Promise<StocktakingApi>;
		postAction?: () => void;
		customHandleError?: (err: unknown) => void;
	}) => {
		performAction({
			action: async () => {
				await confirmation({
					title: t("confirmation.title", { ns: "lib" }),
					message: t("confirmation.message.action", { ns: "lib" }),
				});
				const res = await userAction();
				setStocktaking(res);
				addSuccessFlash(t("lib:common.flash.completed"));

				postAction?.();
			},
			customHandleError,
		});
	};

	const onFetchStocktaking = () =>
		performAction({
			action: async () => {
				const res = await api.organization().getStocktaking(Number(stocktakingId), params);
				setStocktaking(res);
				const pathname = props.location.pathname;

				if (!pathname.includes("drafts") && !pathname.includes("summary")) {
					handleChangeTabTitle(`${res.document_number} | ${t("modules.stocktaking.header.title")}`);
				}
			},
		});

	const onFetchResources = () =>
		performAction({
			action: async () => {
				const fetchParams = {
					resource_name: "STOCKTAKING",
					custom_field_resource_type: "STOCKTAKING",
					custom_field_permission_scope: "WRITE",
				};
				const res = await api.organization().getResourceTypes(fetchParams);
				setCustomFieldsConfig(res.custom_fields);
			},
		});

	if (!stocktaking || !customFieldsConfig) return <LoadingContainer />;

	const goBack = () => history.push(`/${organization.id}/stocktakings`);

	const onStartStocktaking = () =>
		performUserAction({ userAction: () => api.organization().startStocktaking(Number(stocktakingId), params) });

	const onCloseStocktaking = async () =>
		performUserAction({
			userAction: () => api.organization().closeStocktaking(Number(stocktakingId), params),
			customHandleError: async (err) => {
				const errorOrderNotClosed = getErrorOrderNotClosed(err);
				setOrderNotClosedError(errorOrderNotClosed);
			},
		});

	const onCloseCancelStocktaking = () =>
		performUserAction({
			userAction: () => api.organization().closeCancelStocktaking(Number(stocktakingId), params),
		});

	const onCancelStocktaking = () =>
		performUserAction({ userAction: () => api.organization().cancelStocktaking(Number(stocktakingId), params) });

	const onVoidStocktaking = () =>
		performUserAction({
			userAction: () => api.organization().removeStocktaking(Number(stocktakingId), params),
			postAction: goBack,
		});

	const onRestoreStocktaking = () =>
		performUserAction({ userAction: () => api.organization().restoreStocktaking(Number(stocktakingId), params) });

	const renderTitle = () => (
		<>
			{t("modules.stocktaking.header.edit.title")} {stocktaking.document_number}
			<StocktakingStatusLabel status={stocktaking.stocktaking_status} />
		</>
	);

	const buttons: ButtonProps[] = [
		{
			title: t("modules.stocktaking.action.start.title"),
			action: onStartStocktaking,
			variant: "primary",
			hide: stocktaking.stocktaking_status !== "DRAFT",
		},
		{
			title: t("lib:common.action.close"),
			action: onCloseStocktaking,
			variant: "primary",
			hide: stocktaking.stocktaking_status !== "OPEN",
		},
		{
			title: t("common.action.cancel_close"),
			action: onCloseCancelStocktaking,
			variant: "primary",
			hide: stocktaking.stocktaking_status !== "CLOSED",
		},
		{
			title: t("lib:common.action.cancel"),
			action: onCancelStocktaking,
			dropdown: true,
			hide: stocktaking.stocktaking_status !== "OPEN",
		},
		{
			title: stocktaking.stocktaking_status !== "DRAFT" ? t("common.action.void") : t("lib:common.action.remove"),
			action: onVoidStocktaking,
			dropdown: true,
			hide: stocktaking.stocktaking_status === "VOID",
		},
		{
			title: t("lib:common.action.restore"),
			action: onRestoreStocktaking,
			dropdown: true,
			hide: stocktaking.stocktaking_status !== "VOID",
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(true),
			dropdown: true,
		},
	];

	const mobileActions: MobileActionProps[] = [
		{
			title: t("modules.stocktaking.action.start.title"),
			action: onStartStocktaking,
			hide: stocktaking.stocktaking_status !== "DRAFT",
		},
		{
			title: t("lib:common.action.close"),
			action: onCloseStocktaking,
			hide: stocktaking.stocktaking_status !== "OPEN",
		},
		{
			title: t("common.action.cancel_close"),
			action: onCloseCancelStocktaking,
			hide: stocktaking.stocktaking_status !== "CLOSED",
		},
		{
			title: t("lib:common.action.cancel"),
			action: onCancelStocktaking,
			hide: stocktaking.stocktaking_status !== "OPEN",
		},
		{
			title: stocktaking.stocktaking_status !== "DRAFT" ? t("common.action.void") : t("lib:common.action.remove"),
			action: onVoidStocktaking,
			hide: stocktaking.stocktaking_status === "VOID",
		},
		{
			title: t("lib:common.action.restore"),
			action: onRestoreStocktaking,
			hide: stocktaking.stocktaking_status !== "VOID",
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(true),
		},
	];

	return (
		<div className="content">
			<StocktakingContext.Provider
				value={{
					setStocktaking,
					stocktaking,
					setCustomFieldsConfig,
					customFieldsConfig,
				}}
			>
				<Header title={renderTitle()} back={goBack} buttons={isMobile ? [] : buttons} />
				{orderNotClosedError && (
					<OrdersNotClosedAlert
						className="mt-2 mb-4"
						orderNotClosedError={orderNotClosedError}
						stocktaking={stocktaking}
					/>
				)}
				{alertMessage && <Alert variant="warning mt-2 mb-4">{alertMessage}</Alert>}
				<SubmenuStocktakingNavigation stocktakingId={Number(stocktakingId)} />
				{displayNonListMobileActions && <MobileActions actions={mobileActions} />}
				<StocktakingMobileActionsContext.Provider value={mobileActions}>
					<Switch>
						<Route path={`${url}/drafts`} component={StocktakingDraftsTab} />
						<Route path={`${url}/summary`} exact component={StocktakingSummaryTab} />
						{/* <Route path={`${url}/transactions`} exact component={OrganizationTransactionIndexPage} /> */}
						<Route path="" exact component={StocktakingForm} />
					</Switch>
				</StocktakingMobileActionsContext.Provider>
			</StocktakingContext.Provider>
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={stocktaking.id}
					resourceType="STOCKTAKING"
					onHide={() => setShowLogs((prevShowLogs) => !prevShowLogs)}
					organizationId={organization.id}
				/>
			)}
		</div>
	);
};

export default OrganizationStocktakingPreviewPage;
